import styled from 'foundations-theming-specialty/utils';
import { HR } from 'foundations-components/transitional/components/HR';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useOnDelayedHover } from '../utils/useOnDelayedHover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { MOBILE_BREAKPOINT } from '../constants';
import { useMemo } from 'react';
import { usePortalData } from '../data/usePortalData';
import { renderLoadingCell, renderPropertyValue } from '../utils/formatCell';
import { TicketListEmptyState } from './TicketListEmptyState';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Grid = styled.div.withConfig({
  displayName: "TicketListingsGrid__Grid"
})(["display:grid;grid-template-columns:repeat(1,1fr);word-wrap:break-word;@media (min-width:", "px){grid-template-columns:repeat(2,1fr);}@media (min-width:1200px){grid-template-columns:repeat(3,1fr);}@media (min-width:1600px){grid-template-columns:repeat(4,1fr);}@media (min-width:2100px){grid-template-columns:repeat(5,1fr);}gap:", ";color:", ";"], MOBILE_BREAKPOINT, ({
  theme
}) => theme.space[600], ({
  theme
}) => theme.color['text-core-default']);
const DL = styled.dl.withConfig({
  displayName: "TicketListingsGrid__DL"
})(["margin:0;display:grid;grid-template-columns:repeat(2,1fr);grid-template-rows:auto auto;grid-auto-rows:auto;gap:", ";hr{grid-column:1 / -1;}"], ({
  theme
}) => theme.space[400]);
const TitleButton = styled.button.withConfig({
  displayName: "TicketListingsGrid__TitleButton"
})(["background:none;border:none;padding:0;cursor:pointer;text-align:left;color:inherit;font:inherit;overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;&:hover{text-decoration:underline;}"]);
const CardContents = styled.div.withConfig({
  displayName: "TicketListingsGrid__CardContents"
})(["dt{", ";margin-block-end:", ";}dd{", ";margin:0;}&[data-column-id='status']{grid-row:1 / 2;grid-column:1 / 3;}&[data-column-id='subject']{grid-row:2 / 3;grid-column:1 / 3;}&[data-column-id='__requested_by_full_name_or_email']{grid-row:3 / 4;grid-column:1 / 3;}&[data-column-id='subject'] dt,&[data-column-id='status'] dt,&[data-column-id='__requested_by_full_name_or_email'] dt{display:none;}&[data-column-id='subject'] dd{", ";}"], ({
  theme
}) => theme.typography['detail-100'], ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.typography['heading-25'], ({
  theme
}) => theme.typography['heading-400']);
const VISIBLE_TICKET_PROPERTIES = ['createdate', 'hs_lastactivitydate'];
const SPECIAL_TICKET_PROPERTIES = ['status', '__hubspot_owner_full_name', '__requested_by_full_name_or_email', 'hs_pipeline_stage', 'ticket_stage', 'subject', 'content'];
const CardCell = ({
  property,
  row,
  isLoading,
  tooltipTitle,
  stageIsConfigured,
  onClick,
  asTitle
}) => {
  if (!property) return null;
  const cellValue = isLoading ? renderLoadingCell(property) : renderPropertyValue(row[property.propertyName], property, row, true, stageIsConfigured);
  const ddContent = asTitle && onClick ? /*#__PURE__*/_jsx(TitleButton, {
    onClick: onClick,
    children: cellValue
  }) : tooltipTitle ? /*#__PURE__*/_jsx(Tooltip, {
    placement: "left",
    title: tooltipTitle,
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: cellValue
    })
  }) : cellValue;
  return /*#__PURE__*/_jsxs(CardContents, {
    "data-column-id": property.propertyName,
    children: [/*#__PURE__*/_jsx("dt", {
      children: property.displayNameOverride
    }), /*#__PURE__*/_jsx("dd", {
      children: ddContent
    })]
  });
};
export const TicketListingsGrid = ({
  data,
  isLoading,
  onRowClick,
  onRowHover,
  isUsingPillBorderRadius,
  error,
  hasActiveFilters
}) => {
  var _portalData$data3;
  const portalData = usePortalData();
  const configuredTicketListProperties = useMemo(() => {
    var _portalData$data, _portalData$data2;
    if (!((_portalData$data = portalData.data) !== null && _portalData$data !== void 0 && _portalData$data.configuredTicketListProperties)) return [];
    return (_portalData$data2 = portalData.data) === null || _portalData$data2 === void 0 ? void 0 : _portalData$data2.configuredTicketListProperties;
  }, [(_portalData$data3 = portalData.data) === null || _portalData$data3 === void 0 ? void 0 : _portalData$data3.configuredTicketListProperties]);
  const stageIsConfigured = useMemo(() => {
    return configuredTicketListProperties.some(property => property.propertyName === 'hs_pipeline_stage');
  }, [configuredTicketListProperties]);
  const configuredTicketListPropertyByPropertyName = useMemo(() => {
    return configuredTicketListProperties.reduce((acc, property) => {
      acc[property.propertyName] = property;
      return acc;
    }, {});
  }, [configuredTicketListProperties]);
  const tableRowProps = useOnDelayedHover(id => onRowHover(Number(id)));
  const visibleTicketProperties = useMemo(() => {
    const properties = configuredTicketListProperties.filter(property => VISIBLE_TICKET_PROPERTIES.includes(property.propertyName));
    const remainingProperties = configuredTicketListProperties.filter(property => !VISIBLE_TICKET_PROPERTIES.includes(property.propertyName) && !SPECIAL_TICKET_PROPERTIES.includes(property.propertyName));
    return [...properties, ...remainingProperties.slice(0, VISIBLE_TICKET_PROPERTIES.length - properties.length)];
  }, [configuredTicketListProperties]);
  if (!isLoading && data.length === 0) {
    return /*#__PURE__*/_jsx(TicketListEmptyState, {
      error: error,
      hasActiveFilters: hasActiveFilters
    });
  }
  return /*#__PURE__*/_jsx(Grid, {
    role: "list",
    children: data.map(row => {
      var _configuredTicketList;
      return /*#__PURE__*/_jsx(Flex, Object.assign({
        flexDirection: "column",
        gap: "600",
        borderRadius: isUsingPillBorderRadius ? '0' : '400',
        backgroundColor: "fill-surface-default",
        padding: "600",
        boxShadow: "100",
        id: row.hs_ticket_id.toString(),
        role: "listitem"
      }, tableRowProps, {
        children: /*#__PURE__*/_jsxs(DL, {
          children: [/*#__PURE__*/_jsx(CardCell, {
            property: configuredTicketListPropertyByPropertyName['status'],
            row: row,
            isLoading: isLoading,
            stageIsConfigured: stageIsConfigured
          }), /*#__PURE__*/_jsx(CardCell, {
            property: configuredTicketListPropertyByPropertyName['subject'],
            row: row,
            isLoading: isLoading,
            asTitle: true,
            onClick: isLoading ? undefined : () => onRowClick(row.hs_ticket_id)
          }), /*#__PURE__*/_jsx(CardCell, {
            property: configuredTicketListPropertyByPropertyName['__requested_by_full_name_or_email'],
            row: row,
            isLoading: isLoading,
            tooltipTitle: ((_configuredTicketList = configuredTicketListPropertyByPropertyName['__requested_by_full_name_or_email']) === null || _configuredTicketList === void 0 ? void 0 : _configuredTicketList.displayNameOverride) || undefined
          }), visibleTicketProperties.map(property => /*#__PURE__*/_jsxs(CardContents, {
            "data-column-id": property.propertyName,
            children: [/*#__PURE__*/_jsx("dt", {
              children: property.displayNameOverride
            }), /*#__PURE__*/_jsx("dd", {
              children: isLoading ? renderLoadingCell(property) : renderPropertyValue(row[property.propertyName], property, row, true)
            })]
          }, property.propertyName)), /*#__PURE__*/_jsx(HR, {
            marginBlock: "0"
          }), /*#__PURE__*/_jsx(Box, {
            children: /*#__PURE__*/_jsx(CardCell, {
              property: configuredTicketListPropertyByPropertyName['__hubspot_owner_full_name'],
              row: row,
              isLoading: isLoading
            })
          }), /*#__PURE__*/_jsx(Flex, {
            justifyContent: "end",
            children: /*#__PURE__*/_jsx(Button, {
              use: "secondary-alt",
              "aria-label": typeof row.subject === 'string' && row.subject ? unescapedText('customer-portal-app.viewTicketAriaLabel', {
                subject: row.subject
              }) : undefined,
              onClick: () => onRowClick(row.hs_ticket_id),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customer-portal-app.viewTicket"
              })
            })
          })]
        })
      }), row.hs_ticket_id);
    })
  });
};